import { useContext, useEffect, useState } from 'react';
import history from '@history';
import { useDispatch } from 'react-redux';
import { BootstrapContext } from 'app/bootstrap/BootstrapContext';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Controller, useForm } from 'react-hook-form';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import WdPageSimple from '@main/core/WdPageSimple';
import {
  ButtonWithLoader,
  ComposedHeader,
  FormCardLayout,
  MetadataExpandForm,
  TextWithLabel,
} from '@main/components';
import { getKioskById, storeKiosk, updateKiosk } from 'src/app/api/kiosk';
import _ from 'lodash';
import { regexTitle } from 'src/app/utils/validations';
import { useRequest } from '@main/hooks';
import { FormControlLabel, FormLabel, Grid, Switch } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { showMessage } from 'app/store/core/messageSlice';

const Root = styled(WdPageSimple)(({ theme }) => ({
  backgroundColor: '#FFF',
  '& .WdPageSimple-header': {},
  '& .WdPageSimple-content': {},
}));

const schema = yup.object().shape({
  name: yup
    .string()
    .matches(regexTitle, 'El nombre no es válido.')
    .required('El nombre es obligatorio.'),
  status: yup.boolean(),
  description: yup.string(),
});

const defaultValues = {
  name: '',
  company_key: '',
  status: false,
  description: '',
  deviceId: '',
  modelname: '',
  location_key: '',
  hostname: '',
};

const Update = () => {
  const dispatch = useDispatch();
  const { finalizeSession } = useContext(BootstrapContext);
  const [searchParams] = useSearchParams();
  const previousId = searchParams.get('id');

  const [metadata, setMetadata] = useState({});
  const [metadataError, setMetadataError] = useState(false);

  // ----- Inicializacion de validadores de formulario
  const { control, formState, handleSubmit, setValue } = useForm({
    mode: 'onChange',
    defaultValues,
    resolver: yupResolver(schema),
  });
  const { isValid, dirtyFields, errors } = formState;
  // ------ Request Get Admin ------
  const handleOnForbidden = async () => {
    await finalizeSession();
  };

  const handleOnError = async (message) => {
    dispatch(showMessage({ message }));
  };
  const requestOptions = {
    onError: handleOnError,
  };
  const { makeRequest: getPreviousData, response: responsePreviousData } = useRequest(
    getKioskById,
    {
      onForbidden: handleOnForbidden,
      onError: handleOnError,
    }
  );
  // ------ Request Store Location ------
  const { makeRequest: tryStore, isLoading } = useRequest(storeKiosk, requestOptions);
  // ------ Request Update Location ------
  const { makeRequest: tryUpdate, isLoading: isLoadingLocation } = useRequest(
    updateKiosk,
    requestOptions
  );

  const handleLoadToForm = (previousData) => {
    const result = _.omit(previousData, ['updated_at', 'created_at', 'id']);
    Object.keys(result).forEach((keyName) => {
      setValue(keyName, result[keyName], { shouldValidate: true, shouldDirty: false });
    });
  };

  useEffect(() => {
    console.log('PARAMS ', previousId);
    getPreviousData(previousId);
  }, []);

  // ------ Escucha response de request de Admin
  useEffect(() => {
    console.log(responsePreviousData);
    if (!responsePreviousData) {
      return;
    }
    handleLoadToForm(responsePreviousData);
  }, [responsePreviousData]);

  const handleUpdate = (form) => {
    const builtForm = {
      ...form,
      metadata,
    };
    console.log(JSON.stringify(builtForm, null, 3));
    console.log(previousId);
    tryUpdate(previousId, builtForm).then(({ isOk }) => {
      if (isOk) {
        dispatch(
          showMessage({
            message: 'kiosko editada correctamente',
            autoHideDuration: 4000,
            variant: 'success',
          })
        );
        history.replace('/kiosks');
      }
    });
  };

  return (
    <Root
      header={<ComposedHeader decorator="button" />}
      content={
        <Box
          className="flex justify-center items-start"
          sx={{
            width: '100%',
            height: '100%',
          }}
        >
          <FormCardLayout title="Editar kiosko">
            <FormCardLayout.FullColum>
              <form
                name="RegisterEditForm"
                noValidate
                className="flex flex-col justify-center w-full"
                autoComplete="off"
              >
                <div className="flex flex-col">
                  <Controller
                    name="name"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        className="mb-32"
                        label="Nombre"
                        autoFocus
                        type="text"
                        error={!!errors.name}
                        helperText={errors?.name?.message}
                        variant="outlined"
                        required
                        fullWidth
                      />
                    )}
                  />
                  <Controller
                    name="description"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        className="mb-32"
                        label="Descripción"
                        autoFocus
                        type="text"
                        error={!!errors.description}
                        helperText={errors?.description?.message}
                        variant="outlined"
                        required
                        fullWidth
                      />
                    )}
                  />
                  <Controller
                    name="deviceId"
                    control={control}
                    render={({ field }) => (
                      <TextWithLabel title="Id Dispositivo">
                        {field.value ? field.value : 'No asociado'}
                      </TextWithLabel>
                    )}
                  />
                  <Controller
                    name="modelname"
                    control={control}
                    render={({ field }) => (
                      <TextWithLabel title="Modelo">
                        {field.value ? field.value : 'No asociado'}
                      </TextWithLabel>
                    )}
                  />
                  <Controller
                    name="hostname"
                    control={control}
                    render={({ field }) => (
                      <TextWithLabel title="Hostname">
                        {field.value ? field.value : 'No asociado'}
                      </TextWithLabel>
                    )}
                  />
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="company_key"
                        control={control}
                        render={({ field }) => (
                          <TextWithLabel title="Compañia">{field.value}</TextWithLabel>
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="location_key"
                        control={control}
                        render={({ field }) => (
                          <TextWithLabel title="Locación">{field.value}</TextWithLabel>
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="building_key"
                        control={control}
                        render={({ field }) => (
                          <TextWithLabel title="Edificio">{field.value}</TextWithLabel>
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="zone_key"
                        control={control}
                        render={({ field }) => (
                          <TextWithLabel title="Zona">{field.value}</TextWithLabel>
                        )}
                      />
                    </Grid>
                  </Grid>
                  <div className="flex-row justify-center items-center w-full">
                    <Controller
                      name="status"
                      control={control}
                      className="mb-32 mt-32"
                      render={({ field }) => (
                        <div className="flex flex-col justify-center items-center">
                          <FormLabel component="legend">Estatus de la locación</FormLabel>
                          <FormControlLabel
                            className="mb-32"
                            control={
                              <Switch
                                checked={field.value}
                                {...field}
                                inputProps={{ 'aria-label': 'Estatus' }}
                              />
                            }
                            label={field.value ? 'Habilitado' : 'Deshabilitado'}
                          />
                        </div>
                      )}
                    />
                  </div>
                </div>
              </form>
            </FormCardLayout.FullColum>
            <FormCardLayout.FullColum>
              <div>
                <MetadataExpandForm
                  onChange={(metafields) => setMetadata(metafields)}
                  onError={(metaError) => setMetadataError(metaError)}
                />
              </div>
            </FormCardLayout.FullColum>
            <FormCardLayout.RowButton>
              <Button
                onClick={() => history.replace('/kiosks')}
                variant="contained"
                className="w-full md:w-auto"
                color="error"
              >
                Cancelar
              </Button>
              <ButtonWithLoader
                onClick={handleSubmit(handleUpdate)}
                color="success"
                aria-label="Agregar locación"
                variant="contained"
                disabled={_.isEmpty(dirtyFields) || !isValid || metadataError}
                type="submit"
                isLoading={isLoading}
              >
                Actualizar
              </ButtonWithLoader>
            </FormCardLayout.RowButton>
          </FormCardLayout>
        </Box>
      }
    />
  );
};

export default Update;
